/**
 * PostLoginEnrichment.tsx
 * -----------------------
 * Shown immediately after the Welcome splash screen.
 * Calls POST /api/enrichment/run/ which fans out to 4 external APIs:
 *   1. mobile → PAN
 *   2. PAN → personal prefill (DOB, gender, email)
 *   3. PAN → Aadhaar link check
 *   4. PAN → Udhyam / business info
 *
 * If all data is already present in the DB the backend skips the calls
 * and returns immediately, so this screen is near-instant on repeat visits.
 *
 * Usage:
 *   <PostLoginEnrichment onComplete={() => navigate(nextUrl)} />
 */

import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "@/lib/axios";
import { API_BASE_URL } from "@/config";

// ─── Types ───────────────────────────────────────────────────────────────────

interface EnrichmentResult {
  status: string;
  pan: string | null;
  apis_called: string[];
  skipped: string[];
  errors: Record<string, string>;
}

interface Props {
  /** Called once enrichment is done (success or graceful failure).
   *  May return a Promise — the screen stays visible until it resolves. */
  onComplete: () => void | Promise<void>;
}

// ─── Step labels shown in the loader ─────────────────────────────────────────

const STEPS = [
  { key: "mobile_to_pan",       label: "Fetching your PAN details…"         },
  { key: "pan_to_prefill",      label: "Loading personal information…"       },
  { key: "pan_details_plus",    label: "Verifying PAN & KYC details…"        },
  { key: "pan_to_udhyam",       label: "Fetching business / Udyam details…"  },
];

type StepStatus = "pending" | "loading" | "done" | "skipped" | "error";

// ─── Component ───────────────────────────────────────────────────────────────

const PostLoginEnrichment: React.FC<Props> = ({ onComplete }) => {
  const [stepStatuses, setStepStatuses] = useState<Record<string, StepStatus>>(
    () => Object.fromEntries(STEPS.map((s) => [s.key, "pending"]))
  );
  const [currentLabel, setCurrentLabel] = useState("Fetching your information…");
  const [isDone, setIsDone] = useState(false);
  const [isNavigating, setIsNavigating] = useState(false);
  const calledRef = useRef(false);
  const mountedRef = useRef(true);          // tracks whether component is still mounted
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      // Cleanup: mark unmounted and clear any running interval
      mountedRef.current = false;
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, []);

  useEffect(() => {
    if (calledRef.current) return;
    calledRef.current = true;

    const safeSet = <T,>(setter: React.Dispatch<React.SetStateAction<T>>, value: T) => {
      if (mountedRef.current) setter(value);
    };

    const run = async () => {
      // Animate through step labels while the single API call runs
      // Each label shows for 3 s so the user can read it comfortably
      let stepIdx = 0;
      intervalRef.current = setInterval(() => {
        if (!mountedRef.current) {
          if (intervalRef.current) clearInterval(intervalRef.current);
          return;
        }
        if (stepIdx >= STEPS.length) {
          if (intervalRef.current) clearInterval(intervalRef.current);
          return;
        }
        const step = STEPS[stepIdx];
        if (step) {
          safeSet(setCurrentLabel, step.label);
          setStepStatuses((prev) => mountedRef.current ? { ...prev, [step.key]: "loading" } : prev);
        }
        stepIdx++;
      }, 3000);

      try {
        const res = await axios.post<EnrichmentResult>(
          `${API_BASE_URL}/api/enrichment/run/`,
          {},
          { timeout: 30_000 }
        );

        if (intervalRef.current) clearInterval(intervalRef.current);
        if (!mountedRef.current) return;

        const data = res.data;
        const newStatuses: Record<string, StepStatus> = {};

        STEPS.forEach((step) => {
          if (data.apis_called?.includes(step.key)) {
            newStatuses[step.key] = "done";
          } else if (data.skipped?.includes(step.key)) {
            newStatuses[step.key] = "skipped";
          } else if (data.errors?.[step.key]) {
            newStatuses[step.key] = "error";
          } else {
            newStatuses[step.key] = "done";
          }
        });

        safeSet(setStepStatuses, newStatuses);
        safeSet(setCurrentLabel, "Information loaded successfully!");
      } catch (err) {
        if (intervalRef.current) clearInterval(intervalRef.current);
        if (!mountedRef.current) return;
        console.warn("PostLoginEnrichment: enrichment call failed (non-blocking)", err);
        safeSet(setStepStatuses, Object.fromEntries(STEPS.map((s) => [s.key, "done"])));
        safeSet(setCurrentLabel, "Ready to continue…");
      }

      if (!mountedRef.current) return;

      // Show completion tick for 2 s so the user sees it
      safeSet(setIsDone, true);
      await new Promise((r) => setTimeout(r, 2000));

      if (!mountedRef.current) return;

      // Switch label to "Preparing your journey…" while we await navigation
      safeSet(setIsNavigating, true);
      safeSet(setCurrentLabel, "Preparing your journey…");

      // Await the parent's navigation logic — screen stays mounted throughout
      await Promise.resolve(onComplete());
    };

    run();
  }, [onComplete]);

  return (
    <div className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-white" style={{ zIndex: 9999 }}>
      {/* Logo / brand */}
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="mb-8 text-3xl font-bold text-[#065a4e]"
      >
        Intellend
      </motion.div>

      {/* Spinner / check / navigating indicator */}
      <AnimatePresence mode="wait">
        {isNavigating ? (
          <motion.div
            key="navigating"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="mb-6"
          >
            <svg
              className="animate-spin h-10 w-10 text-[#065a4e]"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
          </motion.div>
        ) : !isDone ? (
          <motion.div
            key="spinner"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="mb-6"
          >
            <svg
              className="animate-spin h-10 w-10 text-[#065a4e]"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
              />
            </svg>
          </motion.div>
        ) : !isNavigating ? (
          <motion.div
            key="check"
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: "spring", stiffness: 260, damping: 20 }}
            className="mb-6 flex h-10 w-10 items-center justify-center rounded-full bg-[#065a4e]"
          >
            <svg
              className="h-6 w-6 text-white"
              fill="none"
              stroke="currentColor"
              strokeWidth={3}
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
          </motion.div>
        ) : null}
      </AnimatePresence>

      {/* Current step label */}
      <AnimatePresence mode="wait">
        <motion.p
          key={currentLabel}
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -6 }}
          transition={{ duration: 0.3 }}
          className="mb-6 text-base font-medium text-gray-700"
        >
          {currentLabel}
        </motion.p>
      </AnimatePresence>

      {/* Step progress dots */}
      <div className="flex gap-3">
        {STEPS.map((step) => {
          const s = stepStatuses[step.key];
          return (
            <motion.div
              key={step.key}
              title={step.label}
              animate={{
                scale: s === "loading" ? [1, 1.25, 1] : 1,
              }}
              transition={{ repeat: s === "loading" ? Infinity : 0, duration: 0.8 }}
              className={[
                "h-2.5 w-2.5 rounded-full transition-colors duration-300",
                s === "pending"  ? "bg-gray-200"  : "",
                s === "loading"  ? "bg-[#065a4e]" : "",
                s === "done"     ? "bg-green-500" : "",
                s === "skipped"  ? "bg-gray-400"  : "",
                s === "error"    ? "bg-red-400"   : "",
              ].join(" ")}
            />
          );
        })}
      </div>

      <p className="mt-4 text-xs text-gray-400">
        Fetching your information securely…
      </p>
    </div>
  );
};

export default PostLoginEnrichment;
